import type { EditableTimeEntry, TimeEntry } from "./api"
import { CALENDAR_DAY_MS, calendarMonthRange, utcDayKey } from "./calendar"

// The timesheet's arithmetic, kept pure so it can be tested without a DOM:
// which days a week or month shows, what a period's target is, how its
// numbers are written, and the URL that holds the whole view.
//
// Days are "YYYY-MM-DD" keys throughout, handled in UTC. An entry's day is the
// calendar day it was logged for — stored as UTC midnight and grouped by its
// UTC date on the server — so doing the date arithmetic in the browser's own
// zone would shift every card by one somewhere west of Greenwich.

/** Monday. Matches lib/calendar.ts and the Time Report's date_trunc('week');
 *  YouTrack's Sunday is a locale setting on its instance, not a feature. */
export const WEEK_STARTS_ON = 1
/** A working day and week, as lib/duration.ts counts them: 1d = 8h, 1w = 5d. */
export const WORKDAY_MINUTES = 480
export const WORKWEEK_DAYS = 5

export type TimesheetMode = "week" | "month"

/** Every company you belong to, or only the one the sidebar is on. */
export type TimesheetCompanies = "all" | "current"

export type TimesheetState = {
  /** Any day inside the period shown; ←/→ move it to the first day of the next one. */
  date: string
  mode: TimesheetMode
  projectIds: string[]
  /** Matched against task key and title, server side. */
  q: string
  companies: TimesheetCompanies
}

export type Period = {
  mode: TimesheetMode
  /** The period itself: the week, or the month's first and last day. */
  start: string
  end: string
  /** What the grid draws — the month padded out to whole weeks. */
  gridStart: string
  gridEnd: string
  days: string[]
  inPeriod: (day: string) => boolean
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const msOf = (day: string) => Date.parse(`${day}T00:00:00.000Z`)
const addDays = (day: string, n: number) => utcDayKey(msOf(day) + n * CALENDAR_DAY_MS)

/** A strict calendar day: well-formed and real, so "2026-02-30" is not
 *  quietly read as March 2nd. */
export function isDayKey(value: string | null | undefined): value is string {
  if (!value || !DATE.test(value)) return false
  const ms = msOf(value)
  return !Number.isNaN(ms) && utcDayKey(ms) === value
}

/** Today in the browser's own zone: someone filling in their Friday at 11pm
 *  in São Paulo is filling in Friday, though UTC has moved on to Saturday. */
export function todayKey(now = new Date()) {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export const isWeekend = (day: string) => {
  const weekday = new Date(msOf(day)).getUTCDay()
  return weekday === 0 || weekday === 6
}

function weekStart(day: string) {
  const weekday = new Date(msOf(day)).getUTCDay()
  return addDays(day, -((weekday - WEEK_STARTS_ON + 7) % 7))
}

function daysBetween(from: string, to: string) {
  const days: string[] = []
  for (let day = from; day <= to; day = addDays(day, 1)) days.push(day)
  return days
}

export function periodOf(date: string, mode: TimesheetMode): Period {
  if (mode === "week") {
    const start = weekStart(date)
    const end = addDays(start, 6)
    return {
      mode,
      start,
      end,
      gridStart: start,
      gridEnd: end,
      days: daysBetween(start, end),
      inPeriod: (day) => day >= start && day <= end,
    }
  }
  const at = new Date(msOf(date))
  const first = Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), 1)
  // The calendar view's own grid, so the two month pages pad identically.
  const grid = calendarMonthRange(first)
  const start = utcDayKey(first)
  const end = utcDayKey(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 0))
  return {
    mode,
    start,
    end,
    gridStart: grid.from,
    gridEnd: grid.to,
    days: daysBetween(grid.from, grid.to),
    inPeriod: (day) => day >= start && day <= end,
  }
}

/** The first day of the previous (-1) or next (+1) period — what YouTrack's
 *  date= becomes on ← and →. */
export function shiftPeriod(date: string, mode: TimesheetMode, direction: 1 | -1) {
  if (mode === "week") return addDays(weekStart(date), 7 * direction)
  const at = new Date(msOf(date))
  return utcDayKey(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + direction, 1))
}

// A date input emits every intermediate year while someone types one
// ("0002-…", "0020-…"), so a range can briefly span millennia. Expansion stops
// past a year: far over the 62 records a save allows, so the dialog's "too
// many" hint still shows, without walking hundreds of thousands of days per
// keystroke.
const MAX_RANGE_SPAN_DAYS = 366

/** Weekdays from `from` to `to`, both inclusive (at most a year's worth). */
export function expandRange(from: string, to: string) {
  if (!isDayKey(from) || !isDayKey(to) || from > to) return []
  const days: string[] = []
  for (let day = from, n = 0; day <= to && n <= MAX_RANGE_SPAN_DAYS; day = addDays(day, 1), n++) {
    if (!isWeekend(day)) days.push(day)
  }
  return days
}

export function workingDays(from: string, to: string) {
  return expandRange(from, to).length
}

/** The period's target: its own weekdays only, so October's grid starting on
 *  September 28th still asks for October's 22 days and not 25. */
export function expectedMinutes(period: Pick<Period, "start" | "end">) {
  return workingDays(period.start, period.end) * WORKDAY_MINUTES
}

/** "2w 2d", "1d 4h", "0h" — the period line, in working time. Days and weeks
 *  here because a month is read in them; formatDuration stays hours-only for
 *  the places that compare against an estimate. */
export function formatWork(minutes: number) {
  if (minutes <= 0) return "0h"
  const week = WORKDAY_MINUTES * WORKWEEK_DAYS
  const parts: string[] = []
  let rest = minutes
  const take = (size: number, unit: string) => {
    const n = Math.floor(rest / size)
    rest -= n * size
    if (n) parts.push(`${n}${unit}`)
  }
  take(week, "w")
  take(WORKDAY_MINUTES, "d")
  take(60, "h")
  take(1, "m")
  return parts.join(" ")
}

/** "4h", "2h 30m", "0h 30m" — a card's pill and a day's total. The hours are
 *  always written, as YouTrack does, so a column of pills lines up. */
export function formatCard(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/** A task page's row, in the shape the timesheet's Edit spent time dialog
 *  reads. The row carries no task or company of its own — the page it sits on
 *  is both — and its spentOn is the UTC midnight the server stores, so its day
 *  is the UTC date, as the sheet groups it. */
export function editableEntry(
  entry: Pick<TimeEntry, "id" | "minutes" | "spentOn" | "note">,
  task: { id: string; key: string; title: string },
  company: { id: string; name: string },
): EditableTimeEntry {
  return {
    id: entry.id,
    date: utcDayKey(new Date(entry.spentOn)),
    minutes: entry.minutes,
    note: entry.note,
    task: { id: task.id, key: task.key, title: task.title, companyId: company.id, companyName: company.name },
  }
}

/** Entries grouped by their day, in the order given, with each day's sum. */
export function byDay<T extends { date: string; minutes: number }>(entries: T[]) {
  const days = new Map<string, T[]>()
  const totals = new Map<string, number>()
  for (const entry of entries) {
    days.set(entry.date, [...(days.get(entry.date) ?? []), entry])
    totals.set(entry.date, (totals.get(entry.date) ?? 0) + entry.minutes)
  }
  return { days, totals }
}

/** "October 2026", or "October 19 – 25, 2026" for a week; a week across a
 *  month or year boundary names both ends. */
export function periodLabel(period: Pick<Period, "mode" | "start" | "end">, locale?: string) {
  const start = new Date(msOf(period.start))
  const end = new Date(msOf(period.end))
  const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString(locale, { ...options, timeZone: "UTC" })
  if (period.mode === "month") return format(start, { month: "long", year: "numeric" })
  const year = end.getUTCFullYear()
  if (start.getUTCFullYear() !== year) {
    return `${format(start, { month: "long", day: "numeric", year: "numeric" })} – ${format(end, { month: "long", day: "numeric", year: "numeric" })}`
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    return `${format(start, { month: "long", day: "numeric" })} – ${format(end, { month: "long", day: "numeric" })}, ${year}`
  }
  return `${format(start, { month: "long", day: "numeric" })} – ${end.getUTCDate()}, ${year}`
}

// ---- the URL ----
//
// The whole view is ?date=&mode=&projectIds=&q=, so a reload or a shared link
// opens the same sheet. Anything unreadable falls back to the default rather
// than erroring: a mangled link should still open your current week. There is
// deliberately no author= — the sheet is always the caller's.

export function stateFromParams(
  params: URLSearchParams | Record<string, string | undefined>,
  now = new Date(),
): TimesheetState {
  const get = (key: string) => (params instanceof URLSearchParams ? params.get(key) : (params[key] ?? null))
  const date = get("date")
  const mode = get("mode")
  return {
    date: isDayKey(date) ? date : todayKey(now),
    mode: mode === "month" ? "month" : "week",
    // Only uuid-shaped ids: the server answers anything else with a 400, and
    // a page that will not load is worse than a filter that was dropped.
    projectIds: (get("projectIds") ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter((v) => UUID.test(v)),
    q: (get("q") ?? "").trim(),
    // All is the default — a consultant's week spans clients — so only an
    // explicit "current" narrows it, and anything else reads as all.
    companies: get("companies") === "current" ? "current" : "all",
  }
}

export function paramsFromState(state: TimesheetState): URLSearchParams {
  const params = new URLSearchParams()
  params.set("date", state.date)
  params.set("mode", state.mode)
  if (state.projectIds.length) params.set("projectIds", state.projectIds.join(","))
  if (state.q) params.set("q", state.q)
  if (state.companies === "current") params.set("companies", "current")
  return params
}

/** The GET /timesheet query for a state: the grid's whole span, so padding
 *  days in a month view show their cards too. */
export function sheetQuery(state: TimesheetState) {
  const period = periodOf(state.date, state.mode)
  const params = new URLSearchParams({ from: period.gridStart, to: period.gridEnd })
  if (state.projectIds.length) params.set("projectIds", state.projectIds.join(","))
  if (state.q) params.set("q", state.q)
  params.set("companies", state.companies)
  return params.toString()
}
